/**
 * Type definitions for Embedded Dashboard
 */

import type { DashboardResponse, OrgDeliveryConfigResponse } from '@aica/api-types';
import { parseVoiceSearch } from '@/components/analytics/voice/voice-search';
import { frameOffersDeliverySettings } from '@/lib/embedded-host';

export type Tab = 'dashboard' | 'settings' | 'records' | 'objections' | 'voice';

/**
 * Маршрут standalone-приложения → вкладка встроенного дашборда.
 *
 * Живёт рядом с `Tab`, а не у вызывающего: встроенная оболочка — одна страница
 * с вкладками, и вопрос «есть ли у этого маршрута двойник» должен иметь один
 * ответ. Маршрут, которого здесь нет, `useAppNavigate` откроет отдельным
 * окном — это правильный исход для разделов, которых во встроенной оболочке
 * не существует. Таков и `/settings` во фрейме хоста сообщений (Uspacy): его
 * вкладка настроек доставки скрыта (`embeddedHostOffersDeliverySettings`), и
 * маршрут без вкладки молча привёл бы на вкладку по умолчанию.
 */
export const EMBEDDED_TAB_BY_ROUTE: Readonly<Record<string, Tab>> = {
  '/dashboard': 'dashboard',
  '/records': 'records',
  '/settings': 'settings',
  '/analytics/objections': 'objections',
  '/analytics/voice': 'voice',
};

/**
 * The scope a dashboard card's «Отчёт →» link carries to a stream report: the
 * exact window, its day, the projects and the demo rule (`ObjectionsSearch`).
 * Shared filter of the shell — but a link that names any of them names the
 * whole scope, so the ones it leaves out are cleared (a stale project filter
 * would narrow the numbers the card counted for every project).
 */
const STREAM_SCOPE_PARAMS = ['from', 'to', 'day', 'projectIds', 'demo'] as const;

/**
 * Параметры адреса, которые вкладка встроенной оболочки читает сама: адрес
 * маршрута с ТОЛЬКО этими параметрами имеет двойника. Отчёт «Голос клиента»
 * открывается на своём адресате (`audience`) и в своём разделе
 * (`voiceSection`) — их читает и встроенная вкладка (`parseVoiceSearch`).
 */
const EMBEDDED_TAB_PARAMS: Readonly<Record<string, readonly string[]>> = {
  '/analytics/voice': ['audience', 'voiceSection', ...STREAM_SCOPE_PARAMS],
  '/analytics/objections': [...STREAM_SCOPE_PARAMS],
};

/**
 * Все параметры вкладки, которые принадлежат только ей (остальные — общий
 * фильтр оболочки, он переживает смену вкладки). `useAppNavigate` СЛИВАЕТ
 * `search` ссылки с прежним адресом, поэтому параметр, не названный ссылкой,
 * сбрасывается явно (`undefined` — TanStack не пишет такой ключ в адрес): иначе
 * прежний `audience`, раскрытая строка `row`, разрез `voiceCut` или тема
 * фильтра `voiceTheme` (#2229) подменили бы то, что просили. Относится и к
 * ссылке без запроса.
 */
const EMBEDDED_TAB_OWN_PARAMS: Readonly<Record<string, readonly string[]>> = {
  '/analytics/voice': ['audience', 'voiceSection', 'voiceCut', 'row', 'voiceTheme'],
};

/**
 * Назначение для `useAppNavigate({ embeddedTo })`, или undefined.
 *
 * Адрес С ПАРАМЕТРАМИ двойника не имеет, даже если его путь есть в таблице:
 * параметры называют конкретный раздел, а вкладка показывает свой. `/settings`
 * ведёт на встроенные настройки, но `/settings?section=billing&tab=plan` — это
 * тарифы, которых во встроенной вкладке (настройки доставки в CRM) нет, и
 * отбросить параметры значило бы привести человека не туда, куда он нажал.
 * Исключение — параметры, которые вкладка понимает сама (`EMBEDDED_TAB_PARAMS`):
 * они едут вместе с вкладкой, а названные, но не заданные адресом сбрасываются
 * — прежний `audience` не должен подменить раздел, который просили.
 */
export function embeddedTabTarget(
  to: string
): { to: string; search: EmbeddedTabSearch } | undefined {
  const [path = '', query] = to.split('?', 2);
  const tab = EMBEDDED_TAB_BY_ROUTE[path];
  if (!tab) return undefined;
  if (tab === 'settings' && !frameOffersDeliverySettings()) return undefined;
  const cleared: EmbeddedTabSearch = {
    tab,
    ...Object.fromEntries(
      (EMBEDDED_TAB_OWN_PARAMS[path] ?? []).map((k) => [k, undefined])
    ),
  };
  if (query === undefined) return { to: '/embedded/dashboard', search: cleared };
  const own = EMBEDDED_TAB_PARAMS[path];
  if (!own) return undefined;
  const params = new URLSearchParams(query);
  if ([...params.keys()].some((key) => !own.includes(key))) return undefined;
  const asked = parseVoiceSearch(Object.fromEntries(params));
  const scope = STREAM_SCOPE_PARAMS.some((key) => params.has(key))
    ? {
        from: asked.from,
        to: asked.to,
        day: asked.day,
        // A list as the link spells it: the parser reads "a,b" as it reads an array.
        projectIds: asked.projectIds?.join(','),
        demo: asked.demo,
        // The card has no category: one left in the address would narrow it.
        category: undefined,
      }
    : {};
  return {
    to: '/embedded/dashboard',
    search: {
      ...cleared,
      ...(path === '/analytics/voice'
        ? { audience: asked.audience, voiceSection: asked.voiceSection }
        : {}),
      ...scope,
    },
  };
}

/** The embedded address's parameters a link to a tab sets. */
type EmbeddedTabSearch = {
  tab: Tab;
  audience?: string | undefined;
  voiceSection?: string | undefined;
  voiceCut?: string | undefined;
  row?: string | undefined;
  voiceTheme?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  day?: string | undefined;
  projectIds?: string | undefined;
  demo?: 'yes' | 'no' | undefined;
  category?: undefined;
};

// Both standalone and Bitrix24 shells consume the same validated response.
export type DashboardData = DashboardResponse;

// Delivery configuration type
export interface DeliveryConfig {
  deliverToCrmCard: boolean;
  deliverToSmartProcess: boolean;
  smartProcessId?: string;
  fields: {
    transcript: boolean;
    summary: boolean;
    qualityScore: boolean;
    saleProbability: boolean;
    keywords: boolean;
    sentiment: boolean;
  };
}

// Extended types for Settings
export interface SmartProcess {
  id: string;
  title: string;
  entityTypeId: number;
}

export interface FieldStatus {
  name: string;
  label: string;
  created: boolean;
  fieldId?: string;
}

export interface DeliveryHistoryItem {
  id: string;
  recordId: string;
  status: 'success' | 'failed' | 'pending';
  timestamp: string;
  error?: string;
}

/**
 * What the Settings tab shows of the delivery config
 * (`GET /organizations/:orgId/delivery/config`) read into the tab's terms;
 * the field statuses are their own query. `source` is the server's delivery config as read — a
 * save overlays the tab's switches on it; `null` only for mock data.
 */
export interface DeliveryConfigResponse {
  config: DeliveryConfig;
  smartProcesses: SmartProcess[];
  recentDeliveries: DeliveryHistoryItem[];
  source: OrgDeliveryConfigResponse | null;
}
